import { useEffect, useState } from 'react';
import Button from '@/components/Button';
import SelectBox, { Item } from '@/components/select/SelectBox';
import { OrderIncompleteDeliveryReason } from '@/models/OrderIncompleteDelivery';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ReportIncompleteDeliveryButtonProps {
  onReportIncompleteDelivery: (
    reason: OrderIncompleteDeliveryReason,
    comment?: string,
  ) => Promise<void>;
  reported?: boolean;
}

interface IncompleteDeliveryReasonItem extends Item {
  id: OrderIncompleteDeliveryReason;
  name: string;
}

const reasonOptions: IncompleteDeliveryReasonItem[] = [
  {
    id: OrderIncompleteDeliveryReason.ItemsMissing,
    name: 'Items missing',
  },
  {
    id: OrderIncompleteDeliveryReason.ItemDamaged,
    name: 'Item damaged',
  },
  {
    id: OrderIncompleteDeliveryReason.Other,
    name: 'Other issue',
  },
];

const listReasonOptions = () => Promise.resolve(reasonOptions);

const ReportIncompleteDeliveryButton = ({
  onReportIncompleteDelivery,
  reported = false,
}: ReportIncompleteDeliveryButtonProps) => {
  const [open, setOpen] = useState(false);
  const [showReportedTooltip, setShowReportedTooltip] = useState(false);
  const [reason, setReason] = useState<OrderIncompleteDeliveryReason>(
    OrderIncompleteDeliveryReason.ItemsMissing,
  );
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const closeAndReset = () => {
    setOpen(false);
    setComment('');
    setReason(OrderIncompleteDeliveryReason.ItemsMissing);
  };

  useEffect(() => {
    if (reported) {
      setOpen(false);
      setComment('');
      setReason(OrderIncompleteDeliveryReason.ItemsMissing);
    }
  }, [reported]);

  const handleSubmit = async () => {
    if (reported) {
      return;
    }

    setLoading(true);

    try {
      await onReportIncompleteDelivery(reason, comment.trim() || undefined);
      closeAndReset();
    } finally {
      setLoading(false);
    }
  };

  const reportButton = (
    <Button
      data-testid="order-details-report-incomplete-button"
      variant="secondary"
      icon={
        reported ? (
          <CheckCircleIcon className="h-4 w-4 text-gray-500" />
        ) : (
          <ExclamationTriangleIcon className="h-4 w-4 text-gray-500" />
        )
      }
      disabled={reported}
      onClick={reported ? undefined : () => setOpen(true)}
    >
      {reported ? 'Incomplete reported' : 'Report incomplete'}
    </Button>
  );

  return (
    <>
      {reported ? (
        <span
          className="relative inline-flex"
          tabIndex={0}
          aria-describedby="order-details-report-incomplete-tooltip"
          onMouseEnter={() => setShowReportedTooltip(true)}
          onMouseLeave={() => setShowReportedTooltip(false)}
          onFocus={() => setShowReportedTooltip(true)}
          onBlur={() => setShowReportedTooltip(false)}
        >
          {reportButton}
          {showReportedTooltip && (
            <span
              id="order-details-report-incomplete-tooltip"
              role="tooltip"
              className="absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white opacity-90 shadow-sm"
            >
              This order already has an unresolved incomplete delivery report.
            </span>
          )}
        </span>
      ) : (
        reportButton
      )}

      <Dialog open={open} onClose={closeAndReset} className="fixed inset-0 z-50">
        <div className="flex min-h-screen items-center justify-center bg-black bg-opacity-50 px-4">
          <DialogPanel className="w-full max-w-lg rounded-lg bg-white shadow-lg">
            <DialogTitle className="border-b px-5 py-4 text-lg font-semibold text-gray-900">
              Report incomplete delivery
            </DialogTitle>

            <div className="space-y-5 px-5 py-4">
              <div>
                <div data-testid="order-details-report-incomplete-reason">
                  <SelectBox<IncompleteDeliveryReasonItem>
                    name="incompleteDeliveryReason"
                    label={<span className="block text-sm font-medium text-gray-700">Issue</span>}
                    placeholder="Select issue"
                    queryFn={listReasonOptions}
                    requireSelection
                    defaultValue="first"
                    value={reason}
                    onSelect={(selected) => {
                      if (selected) {
                        setReason(selected.id);
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="comment">
                  Comment
                </label>
                <textarea
                  id="comment"
                  data-testid="order-details-report-incomplete-comment"
                  className="mt-1 h-28 w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={closeAndReset} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  data-testid="order-details-report-incomplete-submit-button"
                  variant="primary"
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Report
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

export default ReportIncompleteDeliveryButton;
